import type { LineActivity } from './MemberPortalControls';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { StaffLineBindingDto, StaffLineUnlinkResultDto } from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
import { useDialogFocus } from '../../hooks/useDialogFocus';

export function MemberLineUnlinkControls({
  memberId,
  onActivity,
  onChanged,
}: {
  memberId: string;
  onActivity?: (activity: LineActivity) => void;
  onChanged?: () => void;
}) {
  const { t } = useTranslation('member');
  const actions = useAuthStore((s) => s.allowedActions);
  const canRead = !!actions?.includes('member:read');
  const canUnlink = canRead && !!actions?.includes('member:line_unlink');
  const visible = canRead && import.meta.env.VITE_MEMBER_PILOT_ENABLED === 'true';
  const [binding, setBinding] = useState<StaffLineBindingDto | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [readRevision, setReadRevision] = useState(0);
  const [opened, setOpened] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const generation = useRef(0);
  const sending = useRef(false);
  const opener = useRef<HTMLButtonElement>(null);
  const reset = () => {
    setName('');
    setPhone('');
    setReason('');
    setChecked(false);
  };
  const close = () => {
    if (sending.current) return;
    if (
      onActivity &&
      (name || phone || reason || checked) &&
      !window.confirm(t('linePanel.discard'))
    )
      return;
    setOpened(false);
    reset();
    requestAnimationFrame(() => opener.current?.focus());
  };
  const dialog = useDialogFocus(opened, close, busy);
  useEffect(() => {
    const ticket = ++generation.current;
    sending.current = false;
    setBusy(false);
    setBinding(null);
    setLoadError(false);
    setOpened(false);
    setMessage('');
    reset();
    if (visible)
      void api
        .get<StaffLineBindingDto>(`/members/${memberId}/line-binding`)
        .then((result) => {
          if (ticket === generation.current) setBinding(result);
        })
        .catch(() => {
          if (ticket === generation.current) setLoadError(true);
        });
    return () => {
      generation.current++;
    };
  }, [memberId, visible, readRevision]);
  useEffect(() => {
    onActivity?.({ busy, dirty: !!(reason || checked || name || phone), dialog: opened });
  }, [busy, reason, checked, name, phone, opened, onActivity]);
  if (!visible) return null;
  if (!binding)
    return (
      <div>
        <p role="status">{t(loadError ? 'linePanel.error' : 'linePanel.loading')}</p>
        {loadError && (
          <button
            type="button"
            className="min-h-11 underline"
            onClick={() => setReadRevision((n) => n + 1)}
          >
            {t('linePanel.retry')}
          </button>
        )}
      </div>
    );
  if (!binding.enabled) return <p role="status">{t('linePanel.disabled')}</p>;
  const eligible =
    binding.bound &&
    binding.hasPhone &&
    binding.bindingId !== null &&
    binding.version !== null &&
    ['ACTIVE', 'REAUTH_REQUIRED'].includes(binding.status ?? '');
  async function submit() {
    if (
      sending.current ||
      !canUnlink ||
      !eligible ||
      !binding?.enabled ||
      !checked ||
      !name.trim() ||
      !phone.trim() ||
      !reason.trim()
    )
      return;
    const ticket = generation.current;
    const snapshot = binding;
    sending.current = true;
    setBusy(true);
    setMessage('');
    try {
      const result = await api.post<StaffLineUnlinkResultDto>(
        `/members/${memberId}/line-binding/unlink`,
        {
          reason: reason.trim(),
          verifiedName: name.trim(),
          verifiedPhone: phone.trim(),
          identityChecked: true,
          expectedVersion: snapshot.version,
          expectedBindingId: snapshot.bindingId,
        },
      );
      if (ticket !== generation.current) return;
      onChanged?.();
      setBinding({ ...snapshot, ...result, bindingId: null });
      setOpened(false);
      reset();
      setMessage(t('lineUnlinkDone'));
      requestAnimationFrame(() => opener.current?.focus());
    } catch {
      if (ticket !== generation.current) return;
      onChanged?.();
      // A conflict or unknown network result requires a read and a fresh human check, never a replay.
      reset();
      setOpened(false);
      setBinding(null);
      setMessage(t('lineUnlinkRetry'));
      try {
        const current = await api.get<StaffLineBindingDto>(`/members/${memberId}/line-binding`);
        if (ticket === generation.current) setBinding(current);
      } catch {
        if (ticket === generation.current) setBinding({ ...snapshot, status: 'UNAVAILABLE' });
      }
      requestAnimationFrame(() => opener.current?.focus());
    } finally {
      if (ticket === generation.current) {
        sending.current = false;
        setBusy(false);
      }
    }
  }
  return (
    <section
      aria-label={t('lineUnlinkTitle')}
      className="mb-4 space-y-3 rounded-md border border-base-300 p-3"
    >
      <p>
        {t(binding.bound ? 'lineUnlinkBound' : 'lineUnlinkUnbound')} · {binding.memberNo} ·{' '}
        {binding.name} · {binding.phone}
      </p>
      <p>{t('lineUnlinkWarning')}</p>
      {canUnlink && (
        <button
          ref={opener}
          type="button"
          disabled={busy || !eligible}
          className="rounded-md bg-error px-4 py-2 text-error-content disabled:opacity-50"
          onClick={() => {
            reset();
            setMessage('');
            setOpened(true);
          }}
        >
          {t('lineUnlinkTitle')}
        </button>
      )}
      {binding.bound && !eligible && <p>{t('lineUnlinkUnavailable')}</p>}
      {message && <p role="status">{message}</p>}
      {opened && canUnlink && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            ref={dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="line-unlink-title"
            tabIndex={-1}
            className="max-h-[90dvh] w-full max-w-lg space-y-4 overflow-auto rounded-lg bg-base-100 p-5"
          >
            <h2 id="line-unlink-title" className="text-lg font-semibold">
              {t('lineUnlinkTitle')}
            </h2>
            <p>{t('lineUnlinkWarning')}</p>
            <p>{t('lineUnlinkRelink')}</p>
            <label className="block">
              {t('lineUnlinkName')}
              <input
                maxLength={64}
                value={name}
                disabled={busy}
                onChange={(e) => setName(e.target.value)}
                className="block w-full rounded border border-base-300 bg-base-100 p-2"
              />
            </label>
            <label className="block">
              {t('lineUnlinkPhone')}
              <input
                maxLength={32}
                value={phone}
                disabled={busy}
                onChange={(e) => setPhone(e.target.value)}
                className="block w-full rounded border border-base-300 bg-base-100 p-2"
              />
            </label>
            <label className="block">
              {t('lineUnlinkReason')}
              <textarea
                maxLength={500}
                value={reason}
                disabled={busy}
                onChange={(e) => setReason(e.target.value)}
                className="block w-full rounded border border-base-300 bg-base-100 p-2"
              />
            </label>
            <label className="flex gap-2">
              <input
                type="checkbox"
                checked={checked}
                disabled={busy}
                onChange={(e) => setChecked(e.target.checked)}
              />
              {t('lineUnlinkChecked')}
            </label>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                disabled={busy}
                onClick={close}
                className="rounded border border-base-300 px-4 py-2"
              >
                {t('lineRebindCancel')}
              </button>
              <button
                type="button"
                disabled={busy || !checked || !name.trim() || !phone.trim() || !reason.trim()}
                onClick={() => void submit()}
                className="rounded bg-error px-4 py-2 text-error-content disabled:opacity-50"
              >
                {t(busy ? 'lineRebindPending' : 'lineUnlinkSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
